import React from "react";
import { useTranslation } from "react-i18next";
import { date } from "@/utils/format";
import { Badge } from "@/components/Badge";
import { Icon } from "@/components/Icon";

const STATUS_VARIANT: Record<
    string,
    "success" | "warning" | "danger" | "neutral" | "info" | "secondary"
> = {
    request: "info",
    planning: "secondary",
    development: "warning",
    testing: "neutral",
    completed: "success",
    cancelled: "danger",
};

const PRIORITY_CONFIG: Record<
    string,
    {
        color: string;
        bg: "success" | "warning" | "danger" | "neutral" | "info" | "secondary";
        arrows: number;
    }
> = {
    low: { color: "text-slate-500", bg: "neutral", arrows: 1 },
    medium: { color: "text-blue-500", bg: "info", arrows: 2 },
    high: { color: "text-amber-500", bg: "warning", arrows: 3 },
    urgent: { color: "text-rose-500", bg: "danger", arrows: 3 },
};

interface DevelopmentInfoProps {
    item: {
        id: number;
        code: string;
        title: string;
        status: string;
        priority: string;
        created_at: string;
        assigned?: { name: string } | null;
        client?: { name: string } | null;
    };
    isIt: boolean;
    setOpenStatusModal: (val: boolean) => void;
    setOpenPriorityModal: (val: boolean) => void;
    setOpenAssigneeModal: (val: boolean) => void;
}

export const DevelopmentInfo: React.FC<DevelopmentInfoProps> = ({
    item,
    isIt,
    setOpenStatusModal,
    setOpenPriorityModal,
    setOpenAssigneeModal,
}) => {
    const { t } = useTranslation("global");

    return (
        <div className="bg-white dark:bg-white/5 rounded-[14px] px-4">
            {/* Date */}
            <div className="flex items-center justify-between py-3.5 border-b border-slate-100 dark:border-white/5">
                <span className="text-sm font-semibold text-slate-700 dark:text-zinc-300">
                    {t("developments.detail.date")}
                </span>
                <div className="">{date(item.created_at)}</div>
            </div>

            {/* Status */}
            <div className="flex items-center justify-between py-3.5 border-b border-slate-100 dark:border-white/5">
                <span className="text-sm font-semibold text-slate-700 dark:text-zinc-300">
                    {t("developments.detail.status")}
                </span>
                <div
                    onClick={() => isIt && setOpenStatusModal(true)}
                    className={
                        isIt
                            ? "cursor-pointer hover:opacity-80 transition-opacity"
                            : ""
                    }
                >
                    <Badge
                        variant={STATUS_VARIANT[item.status] ?? "neutral"}
                        className="py-3 px-3!"
                    >
                        {t(`statuses.${item.status}`)}
                    </Badge>
                </div>
            </div>

            {/* Priority */}
            <div className="flex items-center justify-between py-3.5 border-b border-slate-100 dark:border-white/5">
                <span className="text-sm font-semibold text-slate-700 dark:text-zinc-300">
                    {t("developments.detail.priority")}
                </span>
                <div
                    onClick={() => isIt && setOpenPriorityModal(true)}
                    className={
                        isIt
                            ? "cursor-pointer hover:opacity-80 transition-opacity"
                            : ""
                    }
                >
                    {PRIORITY_CONFIG[item.priority] && (
                        <Badge
                            variant={
                                PRIORITY_CONFIG[item.priority].bg ?? "neutral"
                            }
                        >
                            <span
                                className={`inline-flex items-center ${PRIORITY_CONFIG[item.priority].color}`}
                            >
                                {Array.from({
                                    length: PRIORITY_CONFIG[item.priority]
                                        .arrows,
                                }).map((_, i) => (
                                    <Icon
                                        key={i}
                                        value="arrowUp"
                                        size={12}
                                        strokeWidth={3}
                                    />
                                ))}
                            </span>
                            <span>{t(`priorities.${item.priority}`)}</span>
                        </Badge>
                    )}
                </div>
            </div>

            {/* Assignee */}
            <div className="flex items-center justify-between py-3.5 border-b border-slate-100 dark:border-white/5">
                <span className="text-sm font-semibold text-slate-700 dark:text-zinc-300">
                    {t("developments.detail.assigned")}
                </span>
                <div
                    onClick={() => isIt && setOpenAssigneeModal(true)}
                    className={
                        isIt
                            ? "cursor-pointer hover:opacity-80 transition-opacity"
                            : ""
                    }
                >
                    {item.assigned ? (
                        <Badge
                            variant="transparent"
                            className="bg-slate-100 dark:bg-white/10 border border-slate-200 dark:border-white/15"
                        >
                            <Icon
                                value="user"
                                size={12}
                                className="text-slate-400"
                            />
                            <span>{item.assigned.name}</span>
                        </Badge>
                    ) : (
                        <span className="text-sm text-slate-400">-</span>
                    )}
                </div>
            </div>

            {/* Client */}
            <div className="flex items-center justify-between py-3.5">
                <span className="text-sm font-semibold text-slate-700 dark:text-zinc-300">
                    {t("developments.detail.client")}
                </span>
                {item.client?.name ? (
                    <Badge
                        variant="transparent"
                        className="bg-slate-100 dark:bg-white/10 border border-slate-200 dark:border-white/15"
                    >
                        <Icon
                            value="user"
                            size={12}
                            className="text-slate-400"
                        />
                        <span>{item.client.name}</span>
                    </Badge>
                ) : (
                    <span className="text-sm text-slate-100">-</span>
                )}
            </div>
        </div>
    );
};
